import { cn } from "../utils";
import { Spinner } from "../spinner";
import type { LucideIcon } from "lucide-react";
import { renderClickable, type ClickableProps } from "../lib/clickable";

export type BaseButtonProps = {
  icon?: LucideIcon;
  variant?: "primary" | "secondary" | "white" | "ghost" | "destructive";
  size?: "xs" | "sm" | "md" | "lg";
  loading?: boolean;
  disabled?: boolean;
};

export type ButtonProps = ClickableProps<BaseButtonProps>;

export function Button({
  children,
  icon: Icon,
  variant = "primary",
  size = "md",
  loading = false,
  className,
  disabled,
  to,
  target,
  ...props
}: ButtonProps) {
  const baseStyles = cn(
    "inline-flex items-center gap-x-1.5 rounded-md font-medium shadow-xs",
    "focus-visible:outline-2 focus-visible:outline-offset-2",
    "transition-colors",
  );

  const variantStyles = {
    primary: cn(
      "bg-default-600 text-white hover:bg-default-600/90 focus-visible:outline-default-600",
      "dark:bg-default-500 dark:shadow-none dark:hover:bg-default-400",
    ),
    secondary: cn(
      "bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:outline-gray-600",
      "dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
    ),
    white: cn(
      "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50",
      "dark:bg-white/5 dark:text-white dark:ring-white/10 dark:hover:bg-white/10",
    ),
    ghost: cn(
      "bg-transparent text-gray-700 hover:bg-gray-100 shadow-none",
      "dark:text-gray-300 dark:hover:bg-gray-800",
    ),
    destructive: cn(
      "bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600",
      "dark:bg-red-500 dark:hover:bg-red-400",
    ),
  };

  const sizeStyles = {
    xs: "px-2 py-1 text-xs gap-x-1",
    sm: "px-3 py-2 text-sm gap-x-1.5",
    md: "px-3.5 py-2.5 text-sm gap-x-2",
    lg: "px-4 py-3 text-base gap-x-2",
  };

  const iconSizes = {
    xs: "size-3",
    sm: "size-4",
    md: "size-5",
    lg: "size-6",
  };

  const isDisabled = disabled || loading;

  const classes = cn(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    isDisabled && "opacity-50 cursor-not-allowed pointer-events-none",
    className,
  );

  const content = (
    <>
      {loading ? (
        <Spinner
          size="xs"
          variant={variant === "primary" ? "white" : "default"}
        />
      ) : (
        Icon && (
          <Icon className={cn("-ml-0.5", iconSizes[size])} aria-hidden="true" />
        )
      )}
      {children && <span>{children}</span>}
    </>
  );

  return renderClickable({
    to,
    target,
    disabled: isDisabled,
    content,
    className: classes,
    props,
  });
}
